// src/services/projectService.js - Enhanced project search

import { API_BASE_URL } from '../config';
import { Logger } from '../utils/logger';

const logger = new Logger('ProjectService');

export const searchProjects = async (query, filters = {}) => {
    try {
      // First check if we have a direct project ID pattern
      const projectIdMatch = query.match(/\b[Pp](\d{5,6})\b/);
      if (projectIdMatch) {
        const projectId = `P${projectIdMatch[1]}`;
        return fetchProjectById(projectId);
      }
  
      // If not a direct ID, use the search endpoint
      const searchParams = {
        query,
        ...filters,
        // Include project metadata in response
        includeMetadata: true,
        // Include team members in response
        includeTeamMembers: true,
        // Explicitly request SOW data
        includeSowData: true
      };
      
      logger.debug(`Searching projects with params:`, searchParams);

      const response = await fetch(`${API_BASE_URL}/projects/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchParams)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      logger.debug(`Search results received:`, data);

      // Check if we have results
      if (data.results && Array.isArray(data.results)) {
        if (data.results.length > 0) {
          logger.debug(`First search result:`, data.results[0]);
          logger.debug(`SOW technologies in first result:`, data.results[0].sow_technologies);
          logger.debug(`Description in first result:`, data.results[0].description);
        }

        return processProjectResults(data.results);
      } else if (data.projects && Array.isArray(data.projects)) {
        logger.debug(`Projects found in response:`, data.projects.length);
        return processProjectResults(data.projects);
      } else {
        logger.debug(`Direct array response with ${data.length} items`);
        return processProjectResults(data);
      }
    } catch (error) {
      logger.error('Error searching projects:', error);
      throw error;
    }
  };
  
// Helper function to fetch a project by ID
const fetchProjectById = async (projectId) => {
  try {
    logger.debug(`Fetching project data for ID: ${projectId}`);

    const response = await fetch(`${API_BASE_URL}/projects/${projectId}`);

    if (!response.ok) {
      if (response.status === 404) {
        return []; // Return empty array for not found
      }
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    logger.debug(`Project data received from API:`, data);
    logger.debug(`SOW technologies in API response:`, data.sow_technologies);
    logger.debug(`Description in API response:`, data.description);

    if (!data.sow_technologies) {
      logger.warn(`Project ${projectId} is missing sow_technologies field`);
    }

    if (!data.description) {
      logger.warn(`Project ${projectId} is missing description field`);
    }

    return [data]; // Return as array for consistency with search
  } catch (error) {
    logger.error(`Error fetching project ${projectId}:`, error);
    throw error;
  }
};
  
  // Process and enhance project search results
  const processProjectResults = (results) => {
    if (!results || !Array.isArray(results)) return [];

    return results.map(project => {
      logger.debug(`Processing project result:`, project);
      logger.debug(`SOW technologies in project result:`, project.sow_technologies);
      logger.debug(`Description in project result:`, project.description);

      // Calculate completion percentage if not provided
      if (project.startDate && project.endDate && !project.completionPercentage) {
        const total = new Date(project.endDate) - new Date(project.startDate);
        const elapsed = new Date() - new Date(project.startDate);
        project.completionPercentage = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
      }

      // Extract technologies from team members' skills if not present
      // Only do this if both technologies and sow_technologies are missing
      if (!project.technologies && !project.sow_technologies && project.team && project.team.length > 0) {
        const techSet = new Set();
        project.team.forEach(member => {
          if (member.skills && Array.isArray(member.skills)) {
            member.skills.forEach(skill => techSet.add(skill));
          }
        });
        if (techSet.size > 0) {
          project.technologies = Array.from(techSet);
        }
      }

      // Ensure sow_technologies is preserved if it exists
      if (project.sow_technologies && !Array.isArray(project.sow_technologies)) {
        logger.warn(`Project ${project.project_id} has non-array sow_technologies:`, project.sow_technologies);
        if (typeof project.sow_technologies === 'string') {
          project.sow_technologies = project.sow_technologies.split(',').map(tech => tech.trim());
        } else {
          project.sow_technologies = [];
        }
      }

      // Ensure description is preserved
      if (project.description === undefined) {
        logger.warn(`Project ${project.project_id} is missing description field`);
        project.description = '';
      }

      return project;
    });
  };
